// Authentication Context for Iron Forums

/* eslint-disable react-hooks/set-state-in-effect */
/* eslint-disable react-refresh/only-export-components */

import { createContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import axios from 'axios';
import { authAPI, paymentsAPI, rememberDeviceToken } from '../api/client';
import api from '../api/client';
import type { AuthTokens, TwoFactorPrompt, User } from '../types';
import { isNativePlatform } from '../native';
import {
  isBiometricAvailable,
  isBiometricEnabled,
  enableBiometric,
  disableBiometric,
  authenticateWithBiometric,
  type BiometryType,
} from '../native/biometric';
import { syncPushRegistration, unregisterPush } from '../native/push';
import { clearAppBadge } from '../native/appBadge';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  /** Resolves with a prompt when the account needs an emailed code first. */
  login: (username: string, password: string) => Promise<TwoFactorPrompt | null>;
  completeTwoFactor: (username: string, challenge: string, code: string, rememberDevice: boolean) => Promise<void>;
  loginWithBiometric: () => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  biometricAvailable: boolean;
  biometricEnabled: boolean;
  biometryType: BiometryType;
  enableBiometricLogin: (username: string, refreshToken: string) => Promise<void>;
  disableBiometricLogin: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricEnabled, setBiometricEnabled] = useState(isBiometricEnabled());
  const [biometryType, setBiometryType] = useState<BiometryType>('none');

  const refreshUser = async () => {
    try {
      const userData = await authAPI.getMe();
      setUser(userData);
    } catch (err) {
      // Only treat a genuine auth failure as logged-out. A network error or an
      // aborted request (e.g. a window tearing down mid-flight) must not wipe
      // the shared token and end the session — the 401-refresh interceptor
      // already clears tokens when a refresh truly fails.
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        setUser(null);
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
      }
    }
  };

  useEffect(() => {
    isBiometricAvailable().then((result) => {
      setBiometricAvailable(result.isAvailable);
      setBiometryType(result.biometryType);
    });

    const token = localStorage.getItem('access_token');
    if (token) {
      refreshUser()
        .then(() => { void syncPushRegistration(); })
        .finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, []);

  const login = async (username: string, password: string) => {
    const result = await authAPI.login(username, password);
    if ('two_factor_required' in result) return result;
    await finishLogin(username, result);
    return null;
  };

  const completeTwoFactor = async (username: string, challenge: string, code: string, rememberDevice: boolean) => {
    const tokens = await authAPI.verifyTwoFactor(challenge, code, rememberDevice);
    if (tokens.device_token) rememberDeviceToken(username, tokens.device_token);
    await finishLogin(username, tokens);
  };

  const finishLogin = async (username: string, tokens: AuthTokens) => {
    localStorage.setItem('access_token', tokens.access);
    localStorage.setItem('refresh_token', tokens.refresh);
    await refreshUser();
    if (!isNativePlatform) {
      try {
        await paymentsAPI.getStatus();
        await refreshUser();
      } catch {
        // The route guard uses the last server-authenticated status and the
        // payment screen can retry reconciliation without weakening the gate.
      }
    }
    void syncPushRegistration();

    if (biometricEnabled) {
      try {
        await enableBiometric(username, tokens.refresh);
      } catch {
        // silently continue if credential update fails
      }
    }
  };

  const loginWithBiometric = useCallback(async () => {
    const { refreshToken } = await authenticateWithBiometric();
    const response = await api.post('token/refresh/', { refresh: refreshToken });
    localStorage.setItem('access_token', response.data.access);
    localStorage.setItem('refresh_token', refreshToken);
    await refreshUser();
    if (!isNativePlatform) {
      try {
        await paymentsAPI.getStatus();
        await refreshUser();
      } catch {
        // Keep the authenticated session; the web route guard remains closed
        // when the last known server status still requires payment.
      }
    }
  }, []);

  const enableBiometricLogin = async (username: string, refreshToken: string) => {
    await enableBiometric(username, refreshToken);
    setBiometricEnabled(true);
  };

  const disableBiometricLogin = async () => {
    await disableBiometric();
    setBiometricEnabled(false);
  };

  const logout = async () => {
    await unregisterPush();
    await clearAppBadge();
    const refreshToken = localStorage.getItem('refresh_token');
    if (refreshToken) {
      try {
        await api.post('token/blacklist/', { refresh: refreshToken });
      } catch {
        // Continue with client-side logout even if server-side fails
      }
    }
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        completeTwoFactor,
        loginWithBiometric,
        logout,
        refreshUser,
        biometricAvailable,
        biometricEnabled,
        biometryType,
        enableBiometricLogin,
        disableBiometricLogin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

